import { Stack } from "@mui/material"
import {
    Button,
    Chip,
    copyToClipboard,
    DataTable,
    DialogHandler,
    fetchRoute,
    FullBar,
    GenericWrapperContext,
    getCalendarFormat,
    Header,
    Icon,
    InlineTextEdit,
    TemplateModel,
    Typography
} from "@cakemail-org/ui-components-v2"
import { useTranslation } from "react-i18next"
import "./styles.scss"
import { InlineTagEdit } from "./tagsEdit"
import { useContext, useRef } from "react"
import { getSimpleTemplateTagsValues, ETemplatesFilters } from "../../../../data/tags"
import { getSimpleLanguagesValues } from "../../../../data/language"
import { useNavigate } from "react-router-dom"
import { ERoutes } from "../../../../utils/url"
import { TTagEditChange } from "./types"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"
import { TemplateDetailContainerContext } from "../../context"
import { showDeleteTemplateDialog } from "../../utils"

export function TemplateMainDetails({ isNew }: { isNew?: boolean }) {
    const templateDetailContext = useContext(TemplateDetailContainerContext)
    const genericWrapperContext = useContext(GenericWrapperContext)
    const navigate = useNavigate()
    const { t, i18n } = useTranslation()
    const defaultName = useRef(t("general-untitled"))

    const { template, setTemplate, templateRef, updateTemplate } = templateDetailContext

    function handleDelete() {
        showDeleteTemplateDialog({ template })
            .then(() => {
                navigate(fetchRoute({ route: ERoutes.templates, }))
            })
    }

    function handlePreviewImgClick() {
        if (isNew) {
            updateTemplate({ goToEdit: true })
        }

    }

    function updateTemplateHandler(upTemplate?: TemplateModel, btnRef?: TButtonRef) {
        updateTemplate({ data: upTemplate || template, btnRef, noConfirmation: true })
    }

    function handleEditConfirm({ name, value }: { name?: string, value?: string }) {
        const upTemplate = new TemplateModel(template)

        if (name == "subject") {
            upTemplate.set("content" as keyof TemplateModel, { subject: value })
        } else {
            upTemplate.set(name as keyof TemplateModel, value || "")
        }

        updateTemplateHandler(upTemplate)
    }


    function handleCopyClipBoard() {
        copyToClipboard(String(template.id))
        DialogHandler.show({ title: t("copied-clipboard") })
    }


    function handleChangeTags({ name, value, option, prevValue }: TTagEditChange, btnRef?: TButtonRef) {
        let finalValue: string[] = []

        let upTags = [...template.tags]

        if (name === "type") {
            if (prevValue) {
                upTags = upTags.filter((t) => {
                    return !prevValue.includes(t)
                })
            }
            if (value !== "all") {
                upTags.push(value)
            }
        } else if (name === "language") {
            if (prevValue) {
                upTags = upTags.filter((t) => {
                    return !prevValue.includes(t)
                })
            }
            if (value !== "all") {
                upTags.push(value)
            }
        } else if (option) {
            //For the changes with asset manager Component
            if (value === "tag") {
                upTags.push(option.value)
            } else {
                upTags = upTags.filter((t) => {
                    return t !== option.value
                })
            }
        }

        finalValue = [...new Set(upTags)]
        const upTemplate = new TemplateModel(template)
        upTemplate.tags = finalValue
        setTemplate(upTemplate)
        updateTemplateHandler(upTemplate, btnRef)
    }

    const industriesFilter = getSimpleTemplateTagsValues(ETemplatesFilters.industry)
    const industry: string[] = template.tags?.filter((tag) => industriesFilter.includes(tag))

    const seasonalFilter = getSimpleTemplateTagsValues(ETemplatesFilters.seasonal)
    const seasonal: string[] = template.tags?.filter((tag) => seasonalFilter.includes(tag))

    const usageFilter = getSimpleTemplateTagsValues(ETemplatesFilters.usage)
    const usage: string[] = template.tags?.filter((tag) => usageFilter.includes(tag))


    const languageArray = getSimpleLanguagesValues(genericWrapperContext?.partnerBrand, "sys_lang_", true)
    const language: string = template.tags?.find((tag) => languageArray.includes(tag)) || "sys_lang_en_us"
    const type: string = template.tags?.find((tag) => ["free", "paid"].includes(tag)) || "free"

    const isShared = template.sharing.shared

    const extraFilters = genericWrapperContext?.partnerBrand?.page_customization?.filters
    const templateTagsFilter = getSimpleTemplateTagsValues("templatetags", extraFilters)
    const templateTags: string[] = template.tags?.filter((tag) => templateTagsFilter?.includes(tag))
    const tagDetails = [
        {
            category: <Typography variant="body1L" className="semiBold title">{t("general-language")}</Typography>,
            tags: <InlineTagEdit category="language" value={language} onChange={handleChangeTags} />
        },
        {
            category: <Typography variant="body1L" className="semiBold title">{t("general-industry")}</Typography>,
            tags: <InlineTagEdit category="industry" values={industry} onChange={handleChangeTags} />
        },
        {
            category: <Typography variant="body1L" className="semiBold title">{t("general-seasonal")}</Typography>,
            tags: <InlineTagEdit category="seasonal" values={seasonal} onChange={handleChangeTags} />
        },
        {
            category: <Typography variant="body1L" className="semiBold title">{t("general-usage")}</Typography>,
            tags: <InlineTagEdit category="usage" values={usage} onChange={handleChangeTags} />
        }
    ]

    if (genericWrapperContext.account?.isPartnerOrOrganization()) {
        tagDetails.unshift({
            category: <Typography variant="body1L" className="semiBold title">{t("general-type")}</Typography>,
            tags: <InlineTagEdit category="type" value={type} onChange={handleChangeTags} />
        },)
    }

    if (extraFilters?.templatetags) {
        tagDetails.push({
            category: <Typography variant="body1L" className="semiBold title">{t("general-templateTags")}</Typography>,
            tags: <InlineTagEdit category="templatetags" values={templateTags} onChange={handleChangeTags} extraFilterOptions={extraFilters} />
        })
    }

    const isOwner = isNew === true ? true : template.is_owner
    const sharedToCount = template?.sharing?.accounts?.reduce((sum, a) => { return sum + (a.visible ? 1 : 0) }, 0)

    return <Stack className="templateMainDetails">
        <FullBar
            left={<Header
                key="template-detail-head"
                heading={t("template-detail-heading")}
                headingTooltip={t("template-detail-heading-tooltip", { resellerName: genericWrapperContext?.partnerBrand?.name })}
                support={t("template-detail-support")}
            />}
            right={
                <Button
                    key="template-detial-head-btn"
                    iconOnly
                    variant="outlined"
                    color="body1"
                    onClick={handleDelete}
                    disabled={!isOwner}
                >
                    <Icon name="Delete18" />
                </Button>
            }
        />

        <Stack direction="row" className="mainDetailsContainer">
            <object key={template.thumbnail_url ? "loaded" : ""} data={template.thumbnail_url.replace("/200/110/", "/286/380/")} width="100%" className={isNew ? "isNew" : ""} onClick={handlePreviewImgClick} > <Icon name="Add18" /> </object>
            <Stack className="details">
                <Header
                    heading={
                        <InlineTextEdit
                            disabled={!isOwner}
                            maxRows={2}
                            name="name"
                            onConfirm={handleEditConfirm}
                            tooltipTitle={t("template-detail-edit-name-txt")}
                            defaultValue={defaultName.current === template.name ? "" : template.name}
                            placeholder={t("general-name-placeholder")}
                        />
                    }
                    desc={
                        /*<InlineTextEdit
                            maxRows={5}
                            name="description"
                            placeholder={t("template-detail-edit-desc-placeholder")}
                            onChange={handleDetailsChange}
                            onConfirm={handleEditConfirm}
                            tooltipTitle={t("template-detail-edit-desc-txt")}
                            value={template.description}
                        />*/
                        <InlineTextEdit
                            disabled={!isOwner}
                            maxLength={256}
                            maxRows={3}
                            name="subject"
                            placeholder={t("general-subject-placeholder")}
                            onConfirm={handleEditConfirm}
                            tooltipTitle={t("template-detail-subject-desc-txt")}
                            defaultValue={template.content.subject}
                        />
                    }
                />


                <Stack className="publishedState" direction="row" alignContent="center">
                    {genericWrapperContext.account?.isPartnerOrOrganization() &&
                        <>
                            {isShared ?
                                <Chip label={t(!isOwner ? "general-shared" : "general-published")} icon={<Icon name="Checkmark10" />} />
                                :
                                <>
                                    {isShared === null ?
                                        <Chip label={t("template-sharing-legacy-share")} color="cloud" />
                                        :
                                        <Chip label={t(!isOwner ? "general-unshared" : "general-unpublished")} color="cloud" />
                                    }

                                </>
                            }
                        </>
                    }
                    <Typography onClick={handleCopyClipBoard} variant="body2S" className="uppercase">{t("template-details-asset-id") + ` ${template.id}`} <Icon name="Copy14" color="secondary" /></Typography>
                </Stack>
                <Stack className="detailRows" >
                    {genericWrapperContext.account?.isPartnerOrOrganization() &&
                        <Stack className="detailRow" direction="row">
                            <Typography className="semiBold">{t("template-details-shared-with")}</Typography>
                            <Typography variant="body1L">
                                {template.sharing?.shared ?
                                    <>
                                        {template.sharing.accounts.length === sharedToCount && t("template-details-account-group-all")}
                                        {template.sharing.accounts.length !== sharedToCount && t("template-details-shared-to-x", { count: sharedToCount })}
                                    </>
                                    :
                                    t("template-details-not-shared")
                                }
                            </Typography>
                        </Stack>
                    }
                    <Stack className="detailRow" direction="row" >
                        <Typography className="semiBold">{t("general-created")}</Typography>
                        <Typography variant="body1L">{template.created_on !== 0 ? getCalendarFormat({ unixDate: template.created_on, showTime: false }) : ""}</Typography>
                    </Stack>
                    <Stack className="detailRow" direction="row" >
                        <Typography className="semiBold">{t("general-updated")}</Typography>
                        <Typography variant="body1L">{template.updated_on !== 0 ? getCalendarFormat({ unixDate: template.updated_on, showTime: false }) : ""}</Typography>
                    </Stack>
                </Stack>

            </Stack>
        </Stack>

        <Stack className={`tagsDetails ${isOwner ? "" : "disabled"}`}>
            <DataTable
                columnsData={[
                    { id: "category", label: t("general-category") },
                    { id: "tags", label: t("general-tag_other") },
                    {
                        id: "actions", label: <Button size="small" variant="outlined" color="body1" animated onClick={(e, btnRef) => {
                            const upTemplate = new TemplateModel(template)
                            upTemplate.tags = ["free", "sys_lang_en_us"]
                            upTemplate.tags.push(template.content.type === "bee" ? "bee" : "html")
                            setTemplate(upTemplate)
                            updateTemplateHandler(upTemplate, btnRef)
                        }}>{t("template-details-tags-remove-all")}</Button>
                    }
                ]}
                rows={tagDetails}
                showPagination={false}
            />
        </Stack>
    </Stack>
}